import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isUndefined, isEqual, cloneDeep, isNil } from "lodash";

import AutocompleteSearch from "@plannel/components/AutocompleteSearch";
import routeService from "@plannel/services/data/route-service"
import routingService from "@plannel/services/data/routing-service"
import resourceService from "@plannel/services/data/resource-service"
import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import CalendarGroupRenderer from "@plannel/components/aggrid/CalendarGroupRenderer";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import codeUtil from "@plannel/pages/utils/code-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import NumberUtil from "@plannel/utils/number-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { timeColumnDecimalLimit } = codeUtil;
const Routing = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [routeSearch, setRouteSearch] = useState(reduxViewState?.routeSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  // const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [routing, setRouting] = useState([]);
  const [route, setRoute] = useState([]);
  const [resource, setResource] = useState([]);
  const resourceMap= useRef({});
  const routeMap= useRef({});

  const [resourceSearch, setResourceSearch] = useState(reduxViewState?.resourceSearch ?? null);
  const EXCEL_EXPORT = "excelExport";

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const TIMEUOM = codeUtil.mpTimeUom;
  const TIMEBASE = codeUtil.mpTimeBase;

  const dropdownValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }

    return data.value ? data.value.routeCd : "";
  }

  const dropdownResourceValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }

    return data.value ? data.value.resCd : "";
  }

  const gridFormatter = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  }

  const percentFormatter = (params, isExport = false) => {
    const value = params?.data?.[params?.colDef?.field];
    if (isExport) {
      return NumberUtil.formatNumber(value, { maximumFractionDigits: 4, minimumFractionDigits: 4 });
    }
    return `${value}%`;
  }
  percentFormatter.customType = EXCEL_EXPORT;

  const doubleFormatter = (params) => {
    const colId = params.column.colId;
    // Delete/Backspace clears the cell: store null (not "") so the number cellDataType doesn't render "Invalid Number"
    if (isNil(params.newValue) || params.newValue === "") {
      params.data[colId] = null;
      return true;
    }
    if (isNaN(params.newValue)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InvalidValue") });
      return false;
    }

    // Hide the yield_rate column
    // if (colId === "yieldRate" && (params.newValue > 100 || params.newValue < 0)) {
    //   setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PercentLimit") });
    //   return false;
    // }

    if ((colId === "setupTime" || colId === "runTime" || colId === "jobChangeTime") && params.newValue < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotEnterNegativeValue") });
      return false;
    }
    if (Number(params.newValue) && !Number.isInteger(Number(params.newValue))) {
      const regex = new RegExp(`^\\d*\\.\\d{0,${timeColumnDecimalLimit}}$`);
      if (!regex.test(params.newValue)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_LimitDecimalPoint", { N: timeColumnDecimalLimit }) });
        return false;
      }
    }
    params.data[colId] = params.newValue;
    return true;
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];

    return nullSafeComparator(val1, val2, isDescending);
  }

  const valueFormatter = (param) => {
    if (param.column?.colDef) {
      const { colId } = param.column.getColDef();
      if (colId === "nopCalGrp") {
        return param.value?.name ? param.value.name : t("notConfigured");
      }
    }

    return param.value?.name ? param.value.name : "";
  }

  const MIN_MAX_LOT_SIZE_FIELDS = ["minLotSize", "maxLotSize"];

  const normalizeNullableLotSize = (lotSize) => {
    if (isNil(lotSize) || lotSize === "") {
      return null;
    }

    const parsedValue = Number(lotSize);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const onCellValueChanged = (params) => {
    const { column, node, value } = params;
    const colId = column.getColId();
    if (isEqual(colId, "resCd")) {
      node.data.resId = resourceMap.current[params.value].id;
      node.data.wcCd = resourceMap.current[params.value].workcenterCd;
      node.data.locCd = resourceMap.current[params.value].locCd;
    } else if (isEqual(colId, "routeCd")) {
      node.data.itemCd = routeMap.current[params.value].item?.itemCd;
      node.data.routeName = routeMap.current[params.value].name;
    } else if (isEqual(colId, "timeBase")) {
      if (node.data.lotSize < 1) {
        node.setDataValue("lotSize", 1);
      }
    } else if (isEqual(colId, "lotSize")) {
      if (isNil(value) || value < 1) {
        params.newValue = 1;
      }

      if (node.data.timeBase === "lot") {
        if (!value || value === 0) {
          params.newValue = 1;
        }
      }

      node.setDataValue(colId, params.newValue);
    } else if (MIN_MAX_LOT_SIZE_FIELDS.includes(colId)) {
      const normalizedNewValue = normalizeNullableLotSize(params.newValue);
      const minLotSize = colId === "minLotSize" ? normalizedNewValue : normalizeNullableLotSize(node.data.minLotSize);
      const maxLotSize = colId === "maxLotSize" ? normalizedNewValue : normalizeNullableLotSize(node.data.maxLotSize);

      if (!isNil(minLotSize) && !isNil(maxLotSize) && minLotSize > maxLotSize) {
        node.setDataValue(colId, normalizeNullableLotSize(params.oldValue));
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.inputError,
          content: t("MSG_MinLotSizeCannotExceedMaxLotSize")
        });
      } else {
        node.setDataValue(colId, normalizedNewValue);
      }
    }

    defaultGridMemo.onCellValueChanged(params);
  }

  const columnDefs = [
    {
      headerName: "locCd",
      field: "locCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "wcCd",
      field: "wcCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "itemCd",
      field: "itemCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "routeCd", field: "routeCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: reduxViewState?.route ?? route
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150,
      filterType: 'string',
    },
    {
      headerName: "routeName",
      field: "routeName",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "resCd", field: "resCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownResourceValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownResourceValueFormatter,
        values: reduxViewState?.resource ?? resource
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150,
      filterType: 'string',
    },
    {
      headerName: "jobChangeTime", field: "jobChangeTime",
      type: ["rightAligned"], width: 120,
      valueSetter: doubleFormatter,
      filterType: 'number'
    },
    {
      headerName: "setupTime", field: "setupTime",
      type: ["rightAligned"], width: 120,
      valueSetter: doubleFormatter,
      filterType: 'number'
    },
    {
      headerName: "runTime", field: "runTime",
      type: ["rightAligned"], width: 120,
      valueSetter: doubleFormatter,
      filterType: 'number'
    },
    {
      headerName: "timeUom", field: "timeUom", width: 120,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: {
        values: TIMEUOM
      },
      valueFormatter: gridFormatter,
      filterType: 'string',
      filterOption: TIMEUOM
    },
    {
      headerName: "timeBase", field: "timeBase", width: 120,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: {
        values: TIMEBASE
      },
      valueFormatter: gridFormatter,
      filterType: 'string',
      filterOption: TIMEBASE
    },
    {
      headerName: "nonOperationCalendar", field: "nopCalGrp", colId: "nopCalGrp", type: "nonEditableColumn",
      valueFormatter,
      cellRenderer: CalendarGroupRenderer, cellRendererParams: { setSnackInfo, calType: "NOP" },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 200, filterType: 'string'
    },
    {
      headerName: "lotSize", field: "lotSize",
      type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number'
    },
    {
      headerName: "minLotSize", field: "minLotSize",
      type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number'
    },
    {
      headerName: "maxLotSize", field: "maxLotSize",
      type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number'
    },
    // Hide the yield_rate column
    // {
    //   headerName: "yieldRate", field: "yieldRate",
    //   type: ["rightAligned"], width: 100,
    //   valueFormatter: percentFormatter,
    //   valueSetter: doubleFormatter,
    //   filterType: 'percent'
    // },
    { headerName: "priority", field: "priority", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (routeSearch) {
      searchFilters.routeCd = routeSearch;
    }

    if (resourceSearch) {
      searchFilters.resCd = resourceSearch.resCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const setResourceDto = (result) => {
    for (const row of result) {
      if (row.resCd !== null) {
        row.resourceDto = resourceMap.current[row.resCd];
      }
    }
    return result;
  }

  const computeYieldRate = (results, forward) => {
    if (forward) {
      for(const result of results) {
        result.yieldRate = result.yieldRate * 100;
      }
    } else {
      for(const result of results) {
        if (result.yieldRate > 0) {
          result.yieldRate = result.yieldRate / 100;
        }
      }
    }
    return setResourceDto(results);
  }

  const retrieveRouting = () => {
    const params = getRequestParams();
    routingService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setRouting(computeYieldRate(results, true));
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { routeSearch, resourceSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const retrieveRoute = () => {
    routeService.getLookup()
      .then(response => {
        const route = [];
        const { data } = response;
        for (const res of data) {
          route.push(res.routeCd);
          routeMap.current[res.routeCd] = res;
        }
        setRoute(route);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveResource = () => {
    resourceService.getLookup()
      .then(response => {
        const { results } = response.data;
        const resource = [];
        for (const res of results) {
          resource.push(res.resCd);
          resourceMap.current[res.resCd] = res;
        }
        setResource(resource);
      })
      .catch(e => {
        debug(e);
      });
  }

  const onGridReady = params => {
    retrieveRoute();
    retrieveResource();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const insertRow = () => {
    const newRow = {
      id: null,
      routeCd: null,
      routeName: null,
      resourceDto: {},
      resId: null,
      resCd: null,
      locCd: null,
      wcCd: null,
      itemCd: null,
      jobChangeTime: null,
      setupTime: null,
      runTime: null,
      timeUom: "minutes",
      timeBase: "unit",
      lotSize: 1,
      minLotSize: null,
      maxLotSize: null,
      yieldRate: 100,
      priority: 999,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removeRouting = () => {
    const routeIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        routeIds.push(data.id)
      }
    );
    if (routeIds.length > 0) {
      routingService.remove(routeIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveRouting();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveRouting = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    for (const row of allStateData) {
      if (row.resCd === null) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resource")}] ${t("MSG_FieldRequired")}` });
        return;
      }
      if (row.routeCd === null) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("route")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (row.resId === null && row.resCd) {
        row.resId = resourceMap.current[row.resCd].id;
        row.resourceDto = resourceMap.current[row.resCd];
      }
      if (row.routeCd) {
        row.route = routeMap.current[row.routeCd];
      }
    }
    gridRef.current.api.stopEditing();
    if (allStateData.length > 0) {
      routingService.upsert(computeYieldRate(allStateData, false))
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          // setProgressOn(false);
          retrieveRouting();
        });
    } else {
      // setProgressOn(false)
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      // setProgressOn(true)
      saveRouting();
    } else if (action === "DELETE") {
      removeRouting();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveRouting();
    }
  }

  const getCsvColDefs = () => {
    const csvColDefs = cloneDeep(columnDefs)
    for (const col of csvColDefs) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueFormatter = null;
        col.valueGetter = params => percentFormatter(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    return csvColDefs
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!isUndefined(resource) && !isEmpty(resource)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  useEffect(() => {

    if (!isUndefined(route) && !isEmpty(route)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveRouting();
    }

    return () => {
      setRouting([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    return () => {
      setRouting([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, routeSearch, resourceSearch]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <Autocomplete
          options={route}
          getOptionLabel={option => option}
          value={routeSearch}
          onChange={(_, newValue) => setRouteSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ pr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("route")} />
          )}
        />
        <AutocompleteSearch label="resource" value={resourceSearch} setValue={setResourceSearch} optionLabelProp="name" optionEqualProp="resCd"
          target="resource" />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto", mr: 1 }}>
          <ExcelExportButton title={title} service={routingService} params={getRequestParams()} columnDefs={getCsvColDefs()} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="production-routing-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={routing}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      {/* <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop> */}
    </>
  );
};

export default withTranslation()(Routing);
